import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Send, X, Search, Users, Landmark, Paperclip } from "lucide-react";
import { investorsApi, debtApi, mailApi, emailApi } from "../lib/api";

const MAX_TOTAL = 3.5 * 1024 * 1024; // keep the request small enough to send reliably
const fileToB64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});
const humanSize = (n) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);

export const ComposeEmailModal = ({ open, onClose, outlookConnected, mailbox, dealName, prefill }) => {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (prefill) {
      setTo(prefill.to || "");
      setCc(prefill.cc || "");
      setSubject(prefill.subject || "");
      setName(""); setQ("");
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (!open || loaded) return;
    Promise.all([
      investorsApi.list({ limit: 50000 }).catch(() => []),
      debtApi.list().catch(() => []),
    ]).then(([inv, lenders]) => {
      const a = (inv || []).filter((r) => r.email).map((r) => ({
        name: r.name || r.firm || r.email, email: r.email, firm: r.firm || "", type: r.type || "Investor", src: "Investor CRM",
      }));
      const b = (lenders || []).filter((r) => r.email).map((r) => ({
        name: r.contact || r.lender || r.email, email: r.email, firm: r.lender || "", type: "Lender", src: "Lender CRM",
      }));
      setContacts([...a, ...b]);
      setLoaded(true);
    });
  }, [open, loaded]);

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 2) return [];
    return contacts.filter((c) => [c.name, c.email, c.firm].join(" ").toLowerCase().includes(s)).slice(0, 8);
  }, [q, contacts]);

  const pick = (c) => { setTo(c.email); setName(c.name); setQ(`${c.name} · ${c.email}`); };
  const addCc = (c) => setCc((cur) => {
    const have = cur.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (have.includes(c.email)) return cur;
    return have.concat(c.email).join(", ");
  });

  const addFiles = async (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const current = files.reduce((a, f) => a + f.size, 0);
    const room = MAX_TOTAL - current;
    const picked = [];
    let over = false;
    for (const f of incoming) {
      if (f.size > room - picked.reduce((a, x) => a + x.size, 0)) { over = true; continue; }
      picked.push(f);
    }
    if (over) toast.error(`Attachments must total under ${humanSize(MAX_TOTAL)}`);
    const encoded = await Promise.all(picked.map(async (f) => ({ filename: f.name, contentType: f.type || "application/octet-stream", size: f.size, content: await fileToB64(f) })));
    setFiles((cur) => [...cur, ...encoded]);
  };
  const removeFile = (i) => setFiles((cur) => cur.filter((_, j) => j !== i));

  const send = async () => {
    const addr = to.trim();
    if (!addr || !/.+@.+\..+/.test(addr)) return toast.error("Add a valid recipient email address");
    if (!body.trim()) return toast.error("Write a message first");
    const ccList = cc.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    const badCc = ccList.find((e) => !/.+@.+\..+/.test(e));
    if (badCc) return toast.error(`Not a valid Cc address: ${badCc}`);
    setBusy(true);
    const tid = toast.loading("Sending email…");
    const attachments = files.map((f) => ({ filename: f.filename, content: f.content, contentType: f.contentType }));
    try {
      if (outlookConnected) {
        await mailApi.send({ to_email: addr, subject: subject.trim(), text: body.trim(), name, cc: ccList, attachments });
        toast.success(`Email sent to ${addr}${ccList.length ? ` (cc ${ccList.length})` : ""} from ${mailbox || "Outlook"}${attachments.length ? ` with ${attachments.length} attachment(s)` : ""} — replies land here`, { id: tid });
      } else {
        const r = await emailApi.compose({ to: [{ name, email: addr }], cc: ccList, subject: subject.trim() || "(no subject)", body: body.trim(), attachments, kind: "investor", deal_name: dealName || "" });
        toast[r.delivered ? "success" : "message"](r.delivered ? `Email sent to ${addr}${ccList.length ? ` (cc ${ccList.length})` : ""}${attachments.length ? ` with ${attachments.length} attachment(s)` : ""}` : `Queued to ${addr} (email delivery not configured — logged only)`, { id: tid });
      }
      setTo(""); setCc(""); setName(""); setSubject(""); setBody(""); setQ(""); setFiles([]);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the email", { id: tid });
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="modal-overlay" data-testid="compose-modal" onMouseDown={onClose}>
      <div className="modal-card" style={{ maxWidth: 620, width: "94%" }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {prefill ? "Reply all / follow-up" : "New email"}
          <button className="btn btn-sm" onClick={onClose} data-testid="compose-close"><X size={14} /></button>
        </div>
        <div className="modal-sub">
          {outlookConnected
            ? <>Sends from your Outlook mailbox <b>{mailbox}</b> — replies come straight back to this inbox.</>
            : <>Sends via your firm email. Connect Outlook to have replies land in this inbox.</>}
        </div>

        <div className="compose-body">
          <label className="compose-label">To</label>
          <input className="inp" data-testid="compose-to" placeholder="name@firm.com" value={to}
            onChange={(e) => { setTo(e.target.value); setName(""); }} />

          <label className="compose-label">Cc <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--text3)" }}>— optional, separate multiple with commas</span></label>
          <input className="inp" data-testid="compose-cc" placeholder="cc1@firm.com, cc2@firm.com" value={cc}
            onChange={(e) => setCc(e.target.value)} />

          <label className="compose-label">Or pick a contact from your CRM</label>
          <div className="compose-search">
            <Search size={13} />
            <input className="inp" data-testid="compose-contact-search" placeholder={loaded ? "Search investors & lenders…" : "Loading contacts…"}
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {matches.length > 0 && (
            <div className="compose-results" data-testid="compose-results">
              {matches.map((c) => (
                <div key={`${c.src}-${c.email}`} className="compose-result" data-testid={`compose-contact-${c.email}`}>
                  {c.type === "Lender" ? <Landmark size={13} /> : <Users size={13} />}
                  <button className="compose-result-main" onClick={() => pick(c)} data-testid={`compose-pick-${c.email}`}>
                    <b>{c.name}</b>
                    <span>{c.email}{c.firm ? ` · ${c.firm}` : ""}</span>
                  </button>
                  <span className="compose-result-tag">{c.src === "Lender CRM" ? "Lender" : c.type}</span>
                  <button className="compose-cc-add" onClick={() => addCc(c)} title="Add to Cc" data-testid={`compose-addcc-${c.email}`}>+Cc</button>
                </div>
              ))}
            </div>
          )}

          <label className="compose-label">Subject</label>
          <input className="inp" data-testid="compose-subject" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />

          <label className="compose-label">Message</label>
          <textarea className="inp compose-textarea" data-testid="compose-body" rows={9} placeholder="Write your email…" value={body} onChange={(e) => setBody(e.target.value)} />

          <div className="compose-attach-row">
            <label className="btn btn-sm compose-attach-btn" data-testid="compose-attach-btn">
              <Paperclip size={13} /> Attach document
              <input type="file" multiple style={{ display: "none" }} data-testid="compose-file-input"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            </label>
            {files.length > 0 && <span className="compose-attach-count">{files.length} file(s) · {humanSize(files.reduce((a, f) => a + f.size, 0))}</span>}
          </div>
          {files.length > 0 && (
            <div className="compose-files" data-testid="compose-files">
              {files.map((f, i) => (
                <span key={`${f.filename}-${i}`} className="compose-file-chip" data-testid={`compose-file-${i}`}>
                  <Paperclip size={11} />{f.filename}<em>{humanSize(f.size)}</em>
                  <button onClick={() => removeFile(i)} data-testid={`compose-file-remove-${i}`}><X size={11} /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn" onClick={onClose} data-testid="compose-cancel">Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={busy} data-testid="compose-send"><Send size={13} />{busy ? "Sending…" : "Send email"}</button>
        </div>
      </div>
    </div>
  );
};
